import React, { useState, useRef, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import AdminPageLayout from "../../components/FullPageLayout/AdminPageLayout";
import { useTranslation } from "react-i18next";
import Table from "../../components/Table/Table";
import useTableState from "../../hooks/useTableState";
import { updateTableStatus } from "../../redux/actions/tablesActions";
import isEqual from "lodash/isEqual";
import ValidateFilters from "./ValidateFilters";
import ValidateOrderTypeIcon from "../../components/Icons/ValidateOrderTypeIcon";
import { Button } from "@mui/material";
import { validateStatuses } from "../../constants";
import ButtonGroup from "../../components/Buttons/ButtonGroup";
import { buildValidateOrderDetailsRoute } from "../../routes/AdminRoutes";
import { orderALaCartShop } from "../../routes/AdminRoutes";
import { push } from "connected-react-router";
import usePermissions from "../../redux/selectors/usePermissions";
import useResource from "../../hooks/useResource";
import { useValidateOrders } from "../../hooks/validateOrders";
import { validateOrderStatuses } from "../../constants/index";

const getOrdersTableColumns = ({ columns }) => [
  {
    title: "Type",
    field: "shopType.description",
    defaultSort: columns.shopType.orderDirection,
    defaultFilter: columns.shopType.filterValue,
    customSort: () => 0,
    filterPlaceholder: "shopTypeAll",
    options: {
      sorting: true,
      filtering: true
    },
    cellStyle: {
      width: 40,
      textAlign: "center"
    },
    render: rowData => (
      <ValidateOrderTypeIcon type={rowData.shopType.description} />
    )
  },
  {
    title: "ID",
    field: "id",
    customSort: () => 0,
    defaultSort: columns.id.orderDirection,
    defaultFilter: columns.id.filterValue,
    filterPlaceholder: "idAll",
    options: {
      sorting: true,
      filtering: true
    },
    cellStyle: {
      minWidth: 100
    }
  },
  {
    title: "EmployeeName",
    field: "employeeName",
    customSort: () => 0,
    defaultSort: columns.employeeName.orderDirection,
    defaultFilter: columns.employeeName.filterValue,
    filterPlaceholder: "Employee",
    options: {
      sorting: true,
      filtering: true
    },
    cellStyle: {
      minWidth: 160
    }
  },
  {
    title: "Location",
    field: "location.name",
    customSort: () => 0,
    defaultSort: columns.location.orderDirection,
    defaultFilter: columns.location.filterValue,
    filterPlaceholder: "locationAll",
    options: {
      sorting: true,
      filtering: true
    },
    cellStyle: {
      minWidth: 160
    }
  },
  {
    title: "Status",
    field: "status",
    customSort: () => 0,
    defaultSort: columns.status.orderDirection,
    defaultFilter: columns.status.filterValue,
    filterPlaceholder: "statusAll",
    options: {
      sorting: true,
      filtering: true
    },
    cellStyle: {
      minWidth: 140
    },
    render: rowData =>
      validateStatuses.find(status => status.value === rowData.status).label
  },
  {
    title: "DateCreated",
    field: "createdAt",
    customSort: () => 0,
    defaultSort: columns.dateCreated.orderDirection,
    options: {
      sorting: true
    },
    cellStyle: {
      minWidth: 100
    }
  },
  {
    title: "ProgramRange",
    field: "timeRange",
    customSort: () => 0,
    defaultSort: columns.programRange.orderDirection,
    options: {
      sorting: true
    },
    cellStyle: {
      minWidth: 100
    }
  }
];

const RESOURCE_FIELDS = {
  order: [
    "shop_type",
    "location",
    "timeRange",
    "createdAt",
    "status",
    "employeeName"
  ],
  location: ["name"],
  shopType: ["description"]
};

const RESOURCE_INCLUDE = ["shop_type", "location"];

const stripEmptyFilters = filters => {
  const newFilters = {};
  Object.keys(filters).forEach(filter => {
    if (filters[filter]) {
      newFilters[filter] = filters[filter];
    }
  });
  return newFilters;
};

export default function Validate(props) {
  const dispatch = useDispatch();
  const tableState = useTableState("validateOrders");
  const [ordersFilters, setOrdersFilters] = useState(tableState.filters);
  const tableRef = useRef(null);
  const [isFetched, setIsFetched] = useState(false);
  const { t } = useTranslation();
  const { history } = props;
  const { getValidateOrders } = useValidateOrders();
  const { isAdmin, isSystemAdmin } = usePermissions();

  const apiCallbacks = useMemo(
    () => ({
      getOrders: ({ filters, orderBy, orderDirection, pageSize, page }) => {
        return getValidateOrders({
          filters: stripEmptyFilters(filters || {}),
          orderBy,
          orderDirection,
          page: { size: pageSize, number: page },
          fields: RESOURCE_FIELDS,
          include: RESOURCE_INCLUDE
        });
      }
    }),
    [ordersFilters]
  );
  const [, { getOrders }] = useResource(apiCallbacks);

  useEffect(() => {
    if (tableState.filters !== ordersFilters) updateTable();
  }, [ordersFilters]);

  useEffect(() => {
    setOrdersFilters(tableState.filters);
  }, []);

  const columns = useMemo(() => {
    return getOrdersTableColumns(tableState);
  }, []);

  const fetchOrdersData = async queryData => {
    if (!isFetched) {
      queryData.page = tableState.page;
    }
    dispatch(updateTableStatus("validateOrders", queryData, ordersFilters));
    const filters = { ...ordersFilters };
    queryData.filters.forEach(filter => {
      filters[filter.column.field] = filter.value;
    });

    const result = await getOrders({
      ...queryData,
      orderBy: queryData.orderBy.field,
      filters
    });
    setIsFetched(true);
    // Material UI Refactor
    // Material Table needs the page to be 0 indexed
    result.page -= 1;
    return result;
  };

  const redirectToOrder = orderData => {
    history.push(buildValidateOrderDetailsRoute(orderData.id));
  };

  const updateTable = () => {
    if (tableRef.current) {
      const query = { ...tableRef.current.state.query, page: 0 };
      tableRef.current.onQueryChange(query);
    }
  };

  const handleAddOrderALaCarteShop = () => {
    dispatch(push(orderALaCartShop));
  };

  const expandFiltersByDefault =
    !!tableState.filters.location ||
    !!tableState.filters.shopType ||
    tableState.filters.statusValue !== validateOrderStatuses.incomplete ||
    !!tableState.filters.createdAfter ||
    !!tableState.filters.createdBefore ||
    !!tableState.filters.programRangeStartedBefore ||
    !!tableState.filters.programRangeStartedAfter ||
    !!tableState.filters.searchInput;

  const handleApplyFilters = newFilters => {
    const filters = {
      ...ordersFilters,
      ...newFilters
    };

    if (!isEqual(ordersFilters, filters)) {
      setOrdersFilters(filters);
    }
  };

  const ordersTable = useMemo(() => {
    return (
      <Table
        itemType="record"
        columns={columns}
        data={fetchOrdersData}
        tableRef={tableRef}
        initialPage={tableState.page}
        defaultPerPage={tableState.pageSize}
        onRowClick={(event, orderData) => redirectToOrder(orderData)}
      />
    );
  }, [ordersFilters, isFetched]);

  return (
    <AdminPageLayout title={t("validate.validateDashboardTitle")}>
      <ValidateFilters
        onApplyFilters={handleApplyFilters}
        defaultValues={ordersFilters}
        expandedByDefault={expandFiltersByDefault}
      />
      {(isAdmin || isSystemAdmin) && (
        <ButtonGroup align="left" my={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddOrderALaCarteShop}
          >
            {t("validate.validateOrderALaCarteShopButton")}
          </Button>
        </ButtonGroup>
      )}
      {ordersTable}
    </AdminPageLayout>
  );
}

Validate.propTypes = {
  match: PropTypes.any
};
